<div class="site-content-wrapper">
  <div class="page-header">
    <div class="title">
      <h2>Home Page Assets</h2>
      <p>Upload and manage banner and offer images for different languages.</p>
    </div>
  </div>

  <!-- Upload Form -->
  <div class="upload-form card">
    <h3>Upload New Image</h3>
    
    <form class="form" (ngSubmit)="onFormSubmit()">
      <!-- Step 1: Image Type Selection -->
      <div class="form-step">
        <label class="step-label">1. Select Image Type</label>
        <div class="radio-group">
          <label class="radio-option">
            <input type="radio" name="imageType" value="banner" [(ngModel)]="formData.imageType">
            <div class="radio-content">
              <i class="fas fa-image"></i>
              <span>Banner Image</span>
            </div>
          </label>
          <label class="radio-option">
            <input type="radio" name="imageType" value="offer" [(ngModel)]="formData.imageType">
            <div class="radio-content">
              <i class="fas fa-badge-percent"></i>
              <span>Offer Image</span>
            </div>
          </label>
        </div>
        
        <!-- Size Recommendations -->
        <div class="size-recommendations">
          <div class="recommendation" *ngIf="formData.imageType === 'banner'">
            <i class="fas fa-info-circle"></i>
            <span><strong>Recommended size:</strong> 1905 × 820 pixels</span>
          </div>
          <div class="recommendation" *ngIf="formData.imageType === 'offer'">
            <i class="fas fa-info-circle"></i>
            <span><strong>Recommended size:</strong> 1950 × 150 pixels</span>
          </div>
        </div>
      </div>

      <!-- Step 2: Language Selection -->
      <div class="form-step">
        <label class="step-label">2. Select Language</label>
        <div class="radio-group">
          <label class="radio-option" *ngFor="let lang of languages">
            <input type="radio" name="language" [value]="lang.code" [(ngModel)]="formData.language">
            <div class="radio-content">
              <span>{{ lang.name }}</span>
              <small *ngIf="lang.code === 'en'">Required</small>
            </div>
          </label>
        </div>
      </div>

      <!-- Step 3: File Upload -->
      <div class="form-step">
        <label class="step-label">3. Upload Image</label>
        <div class="upload-area">
          <div class="dropzone"
               (dragover)="$event.preventDefault()"
               (drop)="onFileDrop($event)"
               [class.has-file]="selectedFile">
            <div class="dz-content" *ngIf="!selectedFile">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Drag & drop image here, or</p>
              <label class="link">browse<input type="file" accept="image/*" (change)="onFileSelected($event)" hidden></label>
            </div>
            <div class="file-info" *ngIf="selectedFile">
              <i class="fas fa-file-image"></i>
              <div class="file-details">
                <span class="file-name">{{ selectedFile.name }}</span>
                <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
              </div>
              <button type="button" class="remove-file" (click)="removeFile()">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <!-- Progress -->
          <div class="progress" *ngIf="uploadProgress > 0 && uploadProgress < 100">
            <div class="bar" [style.width.%]="uploadProgress"></div>
            <span class="progress-text">{{ uploadProgress }}%</span>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="form-actions">
        <button type="submit" 
                class="btn btn-primary" 
                [disabled]="!canSubmit() || uploading">
          <i class="fas fa-upload" [class.fa-spin]="uploading"></i>
          {{ uploading ? 'Uploading...' : 'Upload Image' }}
        </button>
      </div>
    </form>
  </div>

  <!-- Current Images Display -->
  <div class="current-images card">
    <div class="card-header">
      <h3>Current Images</h3>
      <button class="btn btn-secondary" (click)="refresh()" [disabled]="loading">
        <i class="fas fa-sync-alt" [class.fa-spin]="loading"></i>
        Refresh
      </button>
    </div>
    
    <div class="images-grouped" *ngIf="assets">
      <!-- Banner Images Group -->
      <div class="image-group">
        <div class="group-header">
          <i class="fas fa-image"></i>
          <h4>Banner Images</h4>
        </div>
        <div class="languages-grid">
          <div class="lang-item" *ngFor="let lang of languages">
            <div class="lang-header">
              <span class="lang-name">{{ lang.name }}</span>
              <span class="status" [class.required]="lang.code === 'en'">
                {{ lang.code === 'en' ? 'Required' : 'Optional' }}
              </span>
            </div>
            <div class="image-preview">
              <div class="preview" *ngIf="assets.banner?.[lang.code]?.url; else noBanner">
                <img [src]="assets.banner[lang.code].url" alt="Banner preview">
                <div class="image-actions">
                  <a class="btn btn-preview" [href]="assets.banner[lang.code].url" target="_blank" rel="noopener">
                    <i class="fas fa-eye"></i>
                    Preview
                  </a>
                  <button class="btn btn-danger" 
                          (click)="deleteImage('banner', lang.code, assets.banner[lang.code])"
                          [disabled]="deleting[lang.code + '_banner']">
                    <i class="fas fa-trash" [class.fa-spin]="deleting[lang.code + '_banner']"></i>
                    {{ deleting[lang.code + '_banner'] ? 'Deleting...' : 'Delete' }}
                  </button>
                </div>
              </div>
              <ng-template #noBanner>
                <div class="no-image">No banner uploaded</div>
              </ng-template>
            </div>
          </div>
        </div>
      </div>

      <!-- Offer Images Group -->
      <div class="image-group">
        <div class="group-header">
          <i class="fas fa-badge-percent"></i>
          <h4>Offer Images</h4>
        </div>
        <div class="languages-grid">
          <div class="lang-item" *ngFor="let lang of languages">
            <div class="lang-header">
              <span class="lang-name">{{ lang.name }}</span>
              <span class="status" [class.required]="lang.code === 'en'">
                {{ lang.code === 'en' ? 'Required' : 'Optional' }}
              </span>
            </div>
            <div class="image-preview">
              <div class="offers-container" *ngIf="assets.offers?.[lang.code]?.length; else noOffer">
                <div class="offer-item" *ngFor="let offer of assets.offers[lang.code]; let i = index">
                  <div class="offer-number">Offer #{{ i + 1 }}</div>
                  <div class="preview">
                    <img [src]="offer.url" alt="Offer preview">
                    <div class="image-actions">
                      <a class="btn btn-preview" [href]="offer.url" target="_blank" rel="noopener">
                        <i class="fas fa-eye"></i>
                        Preview
                      </a>
                      <button class="btn btn-danger" 
                              (click)="deleteImage('offer', lang.code, offer, i)"
                              [disabled]="deleting[lang.code + '_offer_' + i]">
                        <i class="fas fa-trash" [class.fa-spin]="deleting[lang.code + '_offer_' + i]"></i>
                        {{ deleting[lang.code + '_offer_' + i] ? 'Deleting...' : 'Delete' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <ng-template #noOffer>
                <div class="no-image">No offers uploaded</div>
              </ng-template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


